import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Box,
  IconButton,
  Pagination,
  Stack,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { Project } from '../../../models/Project';
import SiteTableRow, { SiteRowData, SiteTableRowMode } from './SiteRow';
import NextScheduleBadge from './NextScheduleBadge';
import OperationProgressBadge from './OperationProgressBadge';
import ServerStatusBadge, { ServerStatusRefreshButton } from './ServerStatusBadge';
import type { DeployTabType } from '../types';
import usePermissions from '../../../hooks/usePermissions';

export type SortKey = 'name' | 'lastDeployedAt';
export type SortDir = 'asc' | 'desc';

interface SiteTableProps {
  rows: SiteRowData[];
  triggerDisabled: boolean;
  page: number; // 1-based
  pageSize: number;
  totalItems: number;
  sortBy: SortKey;
  sortDir: SortDir;
  onPageChange: (page: number) => void;
  onSortChange: (key: SortKey) => void;
  onTrigger: (site: Project) => void;
  onRowClick: (site: Project) => void;
  onCreate: () => void;
  /** Row 모드. 'manage' = /projects(단일 배포 버튼), 'deploy' = /deploy(풀기능). 기본값 'deploy'. */
  mode?: SiteTableRowMode;
  /** site-batch-deploy: opens progress modal when a row has an active batch badge. */
  onOpenBatch?: (site: Project, batchId: number) => void;
  /** site-batch-deploy: stops every service at a site. */
  onStop?: (site: Project) => void;
  /** site-edit-delete-ui: manage 모드에서 사이트 편집 다이얼로그를 연다. */
  onEdit?: (site: Project) => void;
  /** site-edit-delete-ui: manage 모드에서 사이트 삭제 다이얼로그를 연다. */
  onDelete?: (site: Project) => void;
  /**
   * deploy-row-site-navigate: deploy 모드에서 "서비스 이동" 버튼 노출 조건
   * (company 일 때만). SiteRow 로 그대로 전달한다.
   */
  target?: DeployTabType;
  /**
   * dashboard-running-kpi-and-stop-relocate: ref Map keyed by site.id; the
   * RunningSitesKpi banner uses it to scrollIntoView a target row.
   */
  rowRefs?: React.MutableRefObject<Map<string, HTMLElement>>;
}

interface ColumnDef {
  key: SortKey | null;
  label: string;
  align?: 'left' | 'right' | 'center';
}

// lastDeployedAt 정렬은 batch API 도입 전까지 비활성화 (Analysis G-1).
// 활성 현장만 배포 시각 데이터를 가지므로 정렬 의미가 없음.
// manage 모드(현장 목록)에서는 위치/최근 배포 시각이 불필요해 제외한다.
const buildColumns = (mode: SiteTableRowMode): ColumnDef[] => [
  { key: 'name', label: '현장 이름' },
  // 통합서버 정상/비정상 + 영상처리서버 정상 대수 (10분 주기 점검) — ServerStatusBadge.
  ...(mode === 'manage' ? [{ key: null, label: '서버 상태' } as ColumnDef] : []),
  ...(mode === 'manage' ? [{ key: null, label: '라우터 사용 중' } as ColumnDef] : []),
  ...(mode === 'manage' ? [] : [{ key: null, label: '위치' } as ColumnDef]),
  // 현장 관리(manage)에서는 구동(배포) 상태 대신 가장 가까운 일정을 보여준다.
  { key: null, label: '다음 일정' },
  // 운영 프로세스 체크박스 진행률 — OperationProgressBadge 가 렌더.
  { key: null, label: '운영 프로세스' },
  ...(mode === 'manage' ? [] : [{ key: null, label: '최근 배포 시각' } as ColumnDef]),
  { key: null, label: '', align: 'right' },
];

const SiteTable: React.FC<SiteTableProps> = ({
  rows,
  triggerDisabled,
  page,
  pageSize,
  totalItems,
  sortBy,
  sortDir,
  onPageChange,
  onSortChange,
  onTrigger,
  onRowClick,
  onCreate,
  onOpenBatch,
  onStop,
  onEdit,
  onDelete,
  target,
  rowRefs,
  mode = 'deploy',
}) => {
  const theme = useTheme();
  const { canAdmin, canEdit } = usePermissions();
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  // 모바일(md 미만)에서는 6컬럼 테이블이 뭉개지므로 manage 모드에 한해
  // 카드 리스트로 전환한다. useMediaQuery 로 한쪽만 마운트.
  const isMobile = useMediaQuery('(max-width: 999px)');
  const showCards = isMobile && mode === 'manage';
  const columns = buildColumns(mode);

  if (totalItems === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 6,
          textAlign: 'center',
          bgcolor: alpha(theme.palette.background.paper, 0.4),
          border: `1px dashed ${alpha(theme.palette.divider, 0.6)}`,
          borderRadius: 2,
        }}
      >
        <Typography variant="body1" color="text.secondary" gutterBottom>
          {canAdmin
            ? '등록된 현장이 없습니다'
            : '등록된 현장이 없습니다. 관리자에게 현장 추가를 요청하세요.'}
        </Typography>
        {canAdmin && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={onCreate} sx={{ mt: 2 }}>
            신규 현장 등록
          </Button>
        )}
      </Paper>
    );
  }

  if (showCards) {
    return (
      <Box>
        <Stack spacing={1.25}>
          {rows.map(({ site }) => (
            <Paper
              key={site.id}
              variant="outlined"
              onClick={() => onRowClick(site)}
              data-testid={`site-card-${site.id}`}
              sx={{
                p: 1.5,
                borderRadius: 2,
                cursor: 'pointer',
                '&:active': { bgcolor: 'action.hover' },
              }}
            >
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body1" fontWeight={700} noWrap>
                    {site.name_ko?.trim() || site.name}
                  </Typography>
                  {site.name_ko?.trim() && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ fontFamily: 'monospace', display: 'block' }}
                    >
                      {site.name}
                    </Typography>
                  )}
                </Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.25}
                  onClick={(e) => e.stopPropagation()}
                >
                  {onEdit && canEdit && (
                    <IconButton
                      size="small"
                      onClick={() => onEdit(site)}
                      aria-label={`${site.name} 편집`}
                      data-testid={`site-edit-${site.id}`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  {onDelete && canAdmin && (
                    <IconButton
                      size="small"
                      onClick={() => onDelete(site)}
                      aria-label={`${site.name} 삭제`}
                      data-testid={`site-delete-${site.id}`}
                    >
                      <DeleteIcon fontSize="small" color="error" />
                    </IconButton>
                  )}
                  <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </Stack>
              </Stack>

              <Stack spacing={0.75} sx={{ mt: 1 }}>
                <CardField label="서버 상태">
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <ServerStatusBadge siteId={site.id} />
                    <ServerStatusRefreshButton />
                  </Stack>
                </CardField>
                <CardField label="라우터 사용 중">{site.router_in_use_count ?? 0}대</CardField>
                <CardField label="다음 일정">
                  <NextScheduleBadge siteId={site.id} />
                </CardField>
                <CardField label="운영 프로세스">
                  <OperationProgressBadge siteId={site.id} />
                </CardField>
              </Stack>
            </Paper>
          ))}
        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}
        >
          <Typography variant="caption" color="text.secondary">
            총 {totalItems}개 • {page} / {totalPages} 페이지
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, p) => onPageChange(p)}
            color="primary"
            shape="rounded"
            size="small"
          />
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
        }}
      >
        <Table
          size="medium"
          aria-label="site list"
          sx={
            mode === 'manage'
              ? {
                  tableLayout: 'fixed',
                  minWidth: 900,
                  '& .MuiTableCell-root': { px: 1.5, py: 2 },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    py: 1.75,
                    fontSize: 13,
                    whiteSpace: 'nowrap',
                  },
                }
              : undefined
          }
        >
          {mode === 'manage' && (
            <colgroup>
              <col />
              {/* 서버 상태 — 방재실·NVR 표시가 한 줄에 2~3개 들어가게 */}
              <col style={{ width: 270 }} />
              <col style={{ width: 96 }} />
              <col style={{ width: 170 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 104 }} />
            </colgroup>
          )}
          <TableHead>
            <TableRow>
              {columns.map((col, idx) => (
                <TableCell
                  key={`${col.label}-${idx}`}
                  align={
                    mode === 'manage' && col.label === '라우터 사용 중'
                      ? 'center'
                      : col.align || 'left'
                  }
                  sortDirection={col.key && sortBy === col.key ? sortDir : false}
                  sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.background.default, 0.4) }}
                >
                  {col.key ? (
                    <TableSortLabel
                      active={sortBy === col.key}
                      direction={sortBy === col.key ? sortDir : 'asc'}
                      onClick={() => col.key && onSortChange(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : col.label === '서버 상태' ? (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span>{col.label}</span>
                      <ServerStatusRefreshButton />
                    </Stack>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <SiteTableRow
                key={row.site.id}
                site={row.site}
                lastDeployment={row.lastDeployment}
                isActive={row.isActive}
                isInProgress={row.isInProgress}
                activeBatchId={row.activeBatchId ?? null}
                activeCurrentStep={row.activeCurrentStep ?? null}
                activeTotalSteps={row.activeTotalSteps ?? null}
                recentlyCompleted={row.recentlyCompleted ?? false}
                triggerDisabled={triggerDisabled}
                onTrigger={onTrigger}
                onRowClick={onRowClick}
                onOpenBatch={onOpenBatch}
                onStop={onStop}
                onEdit={onEdit}
                onDelete={onDelete}
                target={target}
                rowRefs={rowRefs}
                mode={mode}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          총 {totalItems}개 • {page} / {totalPages} 페이지
        </Typography>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_e, p) => onPageChange(p)}
          color="primary"
          shape="rounded"
          size="small"
        />
      </Stack>
    </Box>
  );
};

/** 모바일 카드의 라벨 + 값 한 줄. 라벨 폭을 고정해 값 정렬을 맞춘다. */
const CardField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={600}
      sx={{ width: 80, flexShrink: 0 }}
    >
      {label}
    </Typography>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Stack>
);

export default SiteTable;
